import { useEffect, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  Tooltip,
} from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NoteEvents } from '~/analytics';
import VoidNoteModal from '~/components/common/VoidNoteModal';
import {
  AttachmentType,
  NoteAttachmentStatus,
  NoteStatus,
  PatientNoteListItemType,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  useDeleteNoteMutation,
  useUpdateVoidReasonMutation,
} from '~/store/services/notes/notes';

import PatientNoteAttachment from './PatientNoteAttachment';
import PatientNoteItemHeader from './PatientNoteItemHeader';
import { DeleteNoteModal } from '~/components/common/DeleteNoteModal';
import { PatientNoteAddendumItem } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteAddendumItem';
import { PatientNoteItemSignedRow } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemSignedRow';
import { PatientNoteItemErrorMessage } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemErrorMessage';
import {
  computeActivityWithHistory,
  getInitialActivityWith,
} from '~/lib/util/notes/notes.util';
import { PatientNoteTreatmentOverviewContainer } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteTreatmentOverviewContainer';
import { PatientNoteItemGroupNoteAttendeesSection } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemGroupNoteAttendeesSection';
import { PatientNoteItemContent } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemContent';
import { PatientNoteItemAddAddendumSection } from './PatientNoteItemAddAddendumSection';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientNoteItemSessionSummary } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemSessionSummary';
import { PatientNoteItemCarePlanContainer } from '~/components/maud/patients/profile/tabs/patientNotesTab/PatientNoteItemCarePlanContainer';

const { neutral } = EquipUIFireflyV2Theme.colors;
interface PatientNoteItemProps {
  confirmedAttendees?: GroupClassEventAttendee[];
  groupClassEventDetails?: GroupClassEventDetails;
  note: PatientNoteListItemType;
}

const groupClassNoteAddAddendumDisabledTooltip =
  "Addendums aren't supported on the patient chart for group class notes. To add an addendum to a group class note, please find the event in your personal schedule.";

const PatientNoteItem = ({
  confirmedAttendees,
  groupClassEventDetails,
  note,
}: PatientNoteItemProps) => {
  // TODO rename?
  const [openVoidNoteModal, setOpenVoidNoteModal] = useState(false);
  const [openDeleteDraftModal, setOpenDeleteDraftModal] = useState(false);

  const [isAddAddendumOpen, setIsAddAddendumOpen] = useState<boolean>(false);

  const {
    addenda,
    attachments,
    groupClassEventExternalId,
    groupSectionEventExternalId,
    noteContent,
    noteExternalId,
    noteStatus,
    noteTemplateRevision,
    noteTemplateRevisionData,
    sessionSummary,
  } = note;

  const TOP_LEVEL_ID = `patient-note-item__${noteExternalId}`;
  const { trackEvent } = useAnalytics();

  const { currentUser, currentPatientId } = useGlobalContext();
  const { carePlanNotesDisplay } = useFlags<LDFlags>();

  const [updateVoidReason, { error: updateVoidReasonError }] =
    useUpdateVoidReasonMutation();
  const [
    deleteNote,
    { isLoading: isDeleteNoteLoading, error: deleteNoteError },
  ] = useDeleteNoteMutation();
  useEffect(() => {
    //scroll add-addendum section into view
    if (isAddAddendumOpen)
      document
        .getElementById(`${TOP_LEVEL_ID}__add-addendum-modal`)
        .scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
  }, [TOP_LEVEL_ID, isAddAddendumOpen]);

  const onVoidConfirm = (voidReason: NoteVoidReason) => {
    const notePayload = {
      noteExternalId,
      voidReason: voidReason.voidReason,
    };
    updateVoidReason({ data: notePayload });
  };

  const isSigned = noteStatus === NoteStatus.SIGNED;

  const uploadedAttachments = useMemo<
    AttachmentType[]
  >((): AttachmentType[] => {
    const noteAttachments = attachments ?? [];
    return noteAttachments.filter(
      (attachment) =>
        attachment.statusCode === NoteAttachmentStatus.UPLOADED ||
        attachment.statusCode == null,
    );
  }, [attachments]);

  const activityWithHistoryMap = useMemo(() => {
    if (!note.addenda) return {};
    const initial = getInitialActivityWith(note?.activityWiths, note.addenda);
    return computeActivityWithHistory(initial, note.addenda).reduce(
      (acc, { timestamp, from, to }) => {
        acc[timestamp] = { from, to };
        return acc;
      },
      {} as Record<string, { from: string[]; to: string[] }>,
    );
  }, [note.activityWiths, note.addenda]);

  return (
    <ChakraBox borderBottom={`1px solid ${neutral.line}`} padding="32px 16px">
      <VoidNoteModal
        isOpen={openVoidNoteModal}
        onConfirm={onVoidConfirm}
        setIsOpen={setOpenVoidNoteModal}
      />
      <DeleteNoteModal
        isDeleteNoteLoading={isDeleteNoteLoading}
        isOpen={openDeleteDraftModal}
        onConfirm={() => {
          deleteNote({
            authoredByProviderExternalId: currentUser.userId,
            inquiryId: null,
            noteId: noteExternalId,
          }).then(() => {
            setOpenDeleteDraftModal(false);
          });
        }}
        setIsOpen={setOpenDeleteDraftModal}
      />

      <PatientNoteItemHeader
        note={note}
        setOpenDeleteDraftModal={setOpenDeleteDraftModal}
        setOpenVoidNoteModal={setOpenVoidNoteModal}
      />

      {!currentPatientId && (
        <PatientNoteItemGroupNoteAttendeesSection
          confirmedAttendees={confirmedAttendees}
          groupClassEventDetails={groupClassEventDetails}
          noteExternalId={noteExternalId}
        />
      )}

      <PatientNoteItemErrorMessage error={updateVoidReasonError} />
      <PatientNoteItemErrorMessage error={deleteNoteError} />

      <PatientNoteTreatmentOverviewContainer note={note} />

      {sessionSummary && (
        <PatientNoteItemSessionSummary
          noteExternalId={noteExternalId}
          sessionSummary={sessionSummary}
        />
      )}

      {carePlanNotesDisplay && <PatientNoteItemCarePlanContainer note={note} />}

      <PatientNoteItemContent
        noteContent={noteContent}
        noteExternalId={noteExternalId}
        noteTemplateRevision={noteTemplateRevision}
        noteTemplateRevisionData={noteTemplateRevisionData}
      />

      {(attachments ?? []).length > 0 && (
        <ChakraFlex flexFlow="row wrap" marginBottom="24px" marginTop="16px">
          {uploadedAttachments.map((attachment: AttachmentType) => (
            <PatientNoteAttachment
              attachment={attachment}
              id={`${TOP_LEVEL_ID}__attachment-id__${attachment.attachmentExternalId}`}
              key={`${TOP_LEVEL_ID}__attachment-id__${attachment.attachmentExternalId}`}
              patientId={currentPatientId}
            />
          ))}
        </ChakraFlex>
      )}

      {isSigned && (
        <PatientNoteItemSignedRow idPrefix={TOP_LEVEL_ID} note={note} />
      )}

      {isSigned &&
        ((!groupClassEventExternalId && !groupSectionEventExternalId) ||
          currentPatientId) && ( // Temporarily hiding adding addendums for group notes, will be enabled later
          <ChakraFlex justifyContent="center">
            <Tooltip
              id={`${TOP_LEVEL_ID}__flag-tooltip`}
              isDisabled={!currentPatientId || !groupClassEventExternalId}
              placement="top"
              tooltipText={groupClassNoteAddAddendumDisabledTooltip}
            >
              <FireflyButton
                id={`${TOP_LEVEL_ID}__add-addendum-button`}
                isDisabled={!!groupClassEventExternalId && !!currentPatientId}
                leftIcon={FAIconName.plusSolid}
                onClick={() => {
                  trackEvent(NoteEvents.addAddendumNotesTabButtonClicked, {
                    Source: AnalyticsSourceValues.NOTES_TAB,
                  });
                  setIsAddAddendumOpen(true);
                }}
                size="small"
                variant="secondary"
              >
                Add addendum
              </FireflyButton>
            </Tooltip>
          </ChakraFlex>
        )}

      {isSigned && (
        <>
          {isAddAddendumOpen && (
            <PatientNoteItemAddAddendumSection
              noteExternalId={noteExternalId}
              setIsOpen={setIsAddAddendumOpen}
            />
          )}
          {addenda?.map((addendum, index) => (
            <PatientNoteAddendumItem
              activityWithChange={
                activityWithHistoryMap[addendum.createdAtDateTime] ?? null
              }
              addendum={addendum}
              idPrefix={TOP_LEVEL_ID}
              index={index}
              isLast={index === addenda?.length - 1}
              key={`patient-note-addenda-${addendum.createdAtDateTime}`}
            />
          ))}
        </>
      )}
    </ChakraBox>
  );
};

export default PatientNoteItem;
